'use client'

import { useCallback, useMemo } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from './api'
import { useToastNotificationStore } from './toast-notifications'

// Database-backed notification (from API)
export interface Notification {
  id: number
  type: string
  title: string
  message: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  sessionId?: string
  handoverRequestId?: number
  metadata?: Record<string, unknown>
  isRead: boolean
  isDismissed: boolean
  createdAt: string
}

interface NotificationState {
  // Database-backed persistent notifications
  notifications: Notification[]
  unreadCount: number
  lastFetchCount: number

  // Settings
  soundEnabled: boolean
  browserNotificationsEnabled: boolean

  // Database-backed notification methods
  fetchNotifications: (options?: {
    includeRead?: boolean
    includeDismissed?: boolean
    limit?: number
    offset?: number
  }) => Promise<void>
  fetchUnreadCount: () => Promise<void>
  markAsRead: (id: number) => Promise<void>
  markAllAsRead: () => Promise<void>
  dismissNotification: (id: number) => Promise<void>
  deleteNotification: (id: number) => Promise<void>
  handleNotificationAction: (notification: Notification) => void
  dismissAll: () => Promise<void>
  markSessionTaken: (sessionId: string) => void

  // Settings & utilities
  setSoundEnabled: (enabled: boolean) => void
  setBrowserNotificationsEnabled: (enabled: boolean) => void
  requestBrowserPermission: () => Promise<boolean>
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      lastFetchCount: 0,
      soundEnabled: true,
      browserNotificationsEnabled: false,

      // ============================================================
      // DATABASE-BACKED NOTIFICATION METHODS (Persistent)
      // ============================================================

      fetchNotifications: async (options = {}) => {
        try {
          console.log(
            '🔍 [DEBUG] fetchNotifications called with options:',
            options
          )
          console.log(
            '🔍 [DEBUG] Current notifications before fetch:',
            get().notifications.length
          )

          const requestParams = {
            includeRead: options.includeRead || false,
            includeDismissed: options.includeDismissed || false,
            limit:
              typeof (options as any).limit === 'number'
                ? (options as any).limit
                : 50,
            offset:
              typeof (options as any).offset === 'number'
                ? (options as any).offset
                : 0,
          }

          // console.log('🔍 [DEBUG] API request params:', requestParams)
          const response = await api.agent.getNotifications(requestParams)
          // console.log('🔍 [DEBUG] API response:', {
          //   success: response.success,
          //   dataCount: response.data?.length || 0,
          // })

          if (response.success && response.data) {
            const notifications = response.data.map((n: any) => ({
              id: n.id,
              type: n.type,
              title: n.title,
              message: n.message,
              priority: n.priority,
              sessionId: n.sessionId || undefined,
              handoverRequestId: n.handoverRequestId || undefined,
              metadata: n.metadata,
              isRead: n.isRead,
              isDismissed: n.isDismissed,
              createdAt: n.createdAt,
            }))

            set({ notifications, lastFetchCount: notifications.length })
          }
        } catch (error) {
          console.error('❌ Failed to fetch notifications:', error)
        }
      },

      fetchUnreadCount: async () => {
        try {
          const response = await api.agent.getUnreadNotificationCount()
          if (response.success && response.data) {
            set({ unreadCount: response.data.count })
          }
        } catch (error) {
          console.error('Failed to fetch unread count:', error)
        }
      },

      markAsRead: async (id: number) => {
        try {
          const response = await api.agent.markNotificationAsRead(id)
          if (response.success) {
            set(state => ({
              notifications: state.notifications.map(n =>
                n.id === id ? { ...n, isRead: true } : n
              ),
              unreadCount: Math.max(0, state.unreadCount - 1),
            }))
          }
        } catch (error) {
          console.error('Failed to mark notification as read:', error)
        }
      },

      markAllAsRead: async () => {
        try {
          const response = await api.agent.markAllNotificationsAsRead()
          if (response.success) {
            set(state => ({
              notifications: state.notifications.map(n => ({
                ...n,
                isRead: true,
              })),
              unreadCount: 0,
            }))
          }
        } catch (error) {
          console.error('Failed to mark all notifications as read:', error)
        }
      },

      dismissNotification: async (id: number) => {
        try {
          console.log('🗑️ [DEBUG] dismissNotification called for id:', id)
          const currentState = get().notifications
          console.log('🔍 [DEBUG] Current notifications before dismiss:', {
            count: currentState.length,
            notifications: currentState.map(n => ({
              id: n.id,
              sessionId: n.sessionId,
              isDismissed: n.isDismissed,
            })),
          })

          const response = await api.agent.dismissNotification(id)
          console.log(
            '📡 [DEBUG] dismissNotification API status:',
            response.success ? 'success' : 'failed'
          )
          if (!response.success) {
            console.error('❌ [DEBUG] dismissNotification API error:', response)
          }

          if (response.success) {
            set(state => {
              const updatedNotifications = state.notifications.map(n =>
                n.id === id ? { ...n, isDismissed: true } : n
              )

              console.log('✅ [DEBUG] Updated notifications after dismiss:', {
                count: updatedNotifications.length,
                notifications: updatedNotifications.map(n => ({
                  id: n.id,
                  sessionId: n.sessionId,
                  isDismissed: n.isDismissed,
                })),
              })

              return { notifications: updatedNotifications }
            })

            console.log('🔄 [DEBUG] Calling fetchUnreadCount after dismiss')
            get().fetchUnreadCount()
          } else {
            console.error(
              '❌ [DEBUG] dismissNotification API failed:',
              response.error
            )
          }
        } catch (error) {
          console.error('❌ [DEBUG] Failed to dismiss notification:', error)
        }
      },

      deleteNotification: async (id: number) => {
        try {
          const response = await api.agent.deleteNotification(id)
          if (response.success) {
            set(state => ({
              notifications: state.notifications.filter(n => n.id !== id),
            }))
            get().fetchUnreadCount()
          }
        } catch (error) {
          console.error('Failed to delete notification:', error)
        }
      },

      dismissAll: async () => {
        try {
          const current = get().notifications
          const idsToDismiss = current
            .filter(n => !n.isDismissed)
            .map(n => n.id)
          await Promise.all(
            idsToDismiss.map(id => api.agent.dismissNotification(id))
          )
          set(state => ({
            notifications: state.notifications.map(n => ({
              ...n,
              isDismissed: true,
            })),
          }))
          get().fetchUnreadCount()
        } catch (error) {
          console.error('Failed to dismiss all notifications:', error)
        }
      },

      markSessionTaken: sessionId => {
        if (!sessionId) {
          return
        }

        set(state => {
          let unreadDelta = 0
          const updated = state.notifications.map(notification => {
            if (notification.sessionId !== sessionId) {
              return notification
            }

            if (!notification.isRead) {
              unreadDelta += 1
            }

            return {
              ...notification,
              isRead: true,
              isDismissed: true,
            }
          })

          return {
            notifications: updated,
            unreadCount: Math.max(0, state.unreadCount - unreadDelta),
          }
        })
      },

      handleNotificationAction: notification => {
        console.log(
          '🔔 [DEBUG] handleNotificationAction called for notification:',
          notification
        )

        // If notification has a sessionId, open chat
        if (notification.sessionId) {
          console.log(
            '🚀 [DEBUG] Opening chat session for notification:',
            notification.sessionId
          )

          // Dispatch custom event to open chat session
          if (typeof window !== 'undefined') {
            const event = new CustomEvent('open-chat-session', {
              detail: { sessionId: notification.sessionId },
            })
            window.dispatchEvent(event)
          }
        }
      },

      setSoundEnabled: enabled => {
        set({ soundEnabled: enabled })
      },

      setBrowserNotificationsEnabled: enabled => {
        set({ browserNotificationsEnabled: enabled })
      },

      requestBrowserPermission: async () => {
        if (!('Notification' in window)) {
          return false
        }

        if (Notification.permission === 'granted') {
          set({ browserNotificationsEnabled: true })
          return true
        }

        if (Notification.permission === 'denied') {
          return false
        }

        const permission = await Notification.requestPermission()
        const granted = permission === 'granted'

        if (granted) {
          set({ browserNotificationsEnabled: true })
        }

        return granted
      },
    }),
    {
      name: 'notification-store',
      partialize: state => ({
        soundEnabled: state.soundEnabled,
        browserNotificationsEnabled: state.browserNotificationsEnabled,
      }),
    }
  )
)

// Hook for easy notification management
export function useNotifications() {
  const notifications = useNotificationStore(state => state.notifications)
  const unreadCount = useNotificationStore(state => state.unreadCount)
  const soundEnabled = useNotificationStore(state => state.soundEnabled)
  const browserNotificationsEnabled = useNotificationStore(
    state => state.browserNotificationsEnabled
  )
  const fetchNotifications = useNotificationStore(
    state => state.fetchNotifications
  )
  const fetchUnreadCount = useNotificationStore(state => state.fetchUnreadCount)
  const markAsRead = useNotificationStore(state => state.markAsRead)
  const markAllAsRead = useNotificationStore(state => state.markAllAsRead)
  const dismissNotification = useNotificationStore(
    state => state.dismissNotification
  )
  const deleteNotification = useNotificationStore(
    state => state.deleteNotification
  )
  const dismissAll = useNotificationStore(state => state.dismissAll)
  const setSoundEnabled = useNotificationStore(state => state.setSoundEnabled)
  const setBrowserNotificationsEnabled = useNotificationStore(
    state => state.setBrowserNotificationsEnabled
  )
  const requestBrowserPermission = useNotificationStore(
    state => state.requestBrowserPermission
  )
  const notificationActions = useNotificationActions()

  return useMemo(
    () => ({
      notifications,
      unreadCount,
      fetchNotifications,
      fetchUnreadCount,
      markAsRead,
      markAllAsRead,
      dismissNotification,
      deleteNotification,
      dismissAll,
      soundEnabled,
      browserNotificationsEnabled,
      setSoundEnabled,
      setBrowserNotificationsEnabled,
      requestBrowserPermission,
      ...notificationActions,
    }),
    [
      notifications,
      unreadCount,
      fetchNotifications,
      fetchUnreadCount,
      markAsRead,
      markAllAsRead,
      dismissNotification,
      deleteNotification,
      dismissAll,
      soundEnabled,
      browserNotificationsEnabled,
      setSoundEnabled,
      setBrowserNotificationsEnabled,
      requestBrowserPermission,
      notificationActions,
    ]
  )
}

export function useNotificationActions() {
  const { addToast } = useToastNotificationStore()

  const showSuccess = useCallback(
    (title: string, message: string) => {
      addToast({ type: 'success', title, message })
    },
    [addToast]
  )

  const showError = useCallback(
    (title: string, message: string) => {
      addToast({
        type: 'error',
        title,
        message,
        urgent: true,
      })
    },
    [addToast]
  )

  const showWarning = useCallback(
    (title: string, message: string) => {
      addToast({ type: 'warning', title, message })
    },
    [addToast]
  )

  const showInfo = useCallback(
    (title: string, message: string) => {
      addToast({ type: 'info', title, message })
    },
    [addToast]
  )

  const showUrgent = useCallback(
    (title: string, message: string) => {
      addToast({
        type: 'error',
        title,
        message,
        urgent: true,
      })
    },
    [addToast]
  )

  return useMemo(
    () => ({ showSuccess, showError, showWarning, showInfo, showUrgent }),
    [showSuccess, showError, showWarning, showInfo, showUrgent]
  )
}

// Export types
export type { NotificationState }
